import {
  Component,
  KeyValueDiffers,
  KeyValueDiffer,
  AfterViewInit,
  DoCheck,
  ElementRef,
  Renderer2,
  OnInit,
  OnDestroy,
  HostListener,
} from "@angular/core";
import { Router } from "@angular/router";
import { ActivatedRoute, Params } from "@angular/router";
import { GlobalsService } from "../app/globals.service";
import { AuthKeyService } from "./authkeys/shared/authkey.service";
import { OwnerService } from "./owners/shared/owner.service";
import { Owner } from "./owners/shared/owner.model";
import { AppService } from "./app.service";
import { Feature } from "./feature.model";
import { debounceTime, fromEvent, Observable, Subscription } from "rxjs";
import { TemplateService } from "./templates/shared/template.service";
import { BindTemplateService } from "./bind/bind-template.service";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.css"],
  providers: [AuthKeyService, OwnerService],
})
export class AppComponent implements AfterViewInit, DoCheck {
  public isDashboardEmbed: boolean = false;
  public loading: boolean;
  public mode: number;
  public ownerList: Owner[];
  public resetPasswordToken: string;
  public targetID: number;
  public title = "app";

  private activeLink: string;
  private ccsReportID: number;
  private dashboardID: number;
  private debug: boolean = false;
  private differ: KeyValueDiffer<string, any>;
  private externalAccountID: number;
  private datasetExternalID: number;
  private hideMenu: boolean;
  private impersonationMode: number = 0;
  private iteration: number;
  private jwt: string;
  private ownerKey: string;
  private parent: string;
  private reportID: number;
  private reportToken: number;
  private theme: string = "default";
  private mouseMoveSubscription: any;
  private mouseClickSubscription: any;
  public constructor(
    private elRef: ElementRef,
    private renderer: Renderer2,
    private route: ActivatedRoute,
    private router: Router,
    private differs: KeyValueDiffers,
    private authKeyService: AuthKeyService,
    private ownerService: OwnerService,
    public bindTemplateService: BindTemplateService,
    public templateService: TemplateService,
    public globalsService: GlobalsService,
    public appService: AppService,
  ) {
    this.globalsService.log("app.component.constructor() ... ");

    this.differ = this.differs.find({}).create();

    // this.globalsService.log(`window.location.href: ${window.location.href}`);

    this.mouseClickSubscription = fromEvent(document, "click")
      .pipe(debounceTime(500))
      .subscribe((e: MouseEvent) => {
        this.callKeepalive();
      });

    this.mouseMoveSubscription = fromEvent(document, "mousemove")
      .pipe(debounceTime(1000))
      .subscribe((e: MouseEvent) => {
        this.callKeepalive();
      });

    // Angular bug.  see https://github.com/angular/angular/issues/12157
    var href = window.location.href;
    this.isDashboardEmbed = href.toLowerCase().includes("/dashboard/v3/view");
    const hasParams = href.includes("?");
    const hasJWT = hasParams && href.includes("jwt=");

    if (this.isDashboardEmbed) {
      this.globalsService.setShowLogin(false);
      this.globalsService.setHideMenu(true);
    } else if (hasJWT) {
      var search = window.location.search;
      var params = new URLSearchParams(search);
      var jwt = params.get("jwt");

      this.globalsService.setJWT(jwt);
      this.globalsService.setShowLogin(true);
    } else if (hasParams) {
      if (href.includes("simple=true")) {
        this.globalsService.setSimple(true);
      }
      if (href.includes("library=true")) {
        this.globalsService.setLibraryPreview(true);
      }
      this.globalsService.setShowLogin(false);
    } else {
      this.globalsService.setShowLogin(true);
    }

    this.route.queryParams.subscribe((params: Params) => {
      if (this.globalsService.getResetPasswordToken() == null) {
        this.configState(params);
      }
      this.setTheme();
    });

    console.log(`currentUrl: ${this.globalsService.getCurrentURL()}`);

    if (href.includes("reports-view")) {
      var search = window.location.search;
      var params = new URLSearchParams(search);

      const k = params.get("k");
      const aid = params.get("aid");
      const uid = params.get("uid"); // => respondent.externalID
      const reportID = params.get("reportID");

      var bulkSeed = 1000000;

      if (reportID) {
        var rid = Number(reportID);

        if (rid >= bulkSeed) {
          var btvid = rid - bulkSeed;

          if (k && aid && uid && btvid) {
            this.router.navigateByUrl(
              `/preview-bulk?k=${k}&aid=${aid}&uid=${uid}&btvid=${btvid}`,
            );
          }
        }
      }
    }

    // TODO fix this hack
    if (href.includes("guards/guard-request/")) {
      // Use URLSearchParams to parse and forward any needed query params
      const url = new URL(href);
      const uid = url.searchParams.get("uid");
      const k = url.searchParams.get("k");
      const taskId = url.pathname.split("/").pop(); // last segment

      if (uid && taskId) {
        this.router.navigate(["/guards/guard-request", taskId], {
          queryParams: { k, uid },
        });
      }
      return;
    }

    if (this.globalsService.isSimple()) {
      // if simple=true and there's no site id or report id specified in the url,
      // load features and go to simple admin
      if (href.indexOf("reports-view") < 0 && href.indexOf("sid=") < 0) {
        this.globalsService.loadFeatureSet(href);
        this.router.navigateByUrl("/");
      }
    }
  }

  public callKeepalive() {
    this.globalsService.broadcast('{"ping":true}');
    //this.globalsService.log('callKeepalive');
  }

  @HostListener("window:message", ["$event"])
  onMessage(event: any) {
    const prefix = "cewm: [app, received]; ";

    try {
      if (event.data) {
        this.globalsService.log(`${prefix} ${event.data}`);
      }
    } catch (e) {
      if (this.globalsService.isDebug()) {
        this.globalsService.log(
          "preview.component.onMessage: could not parse json.",
        );
      }
    }
  }

  public showSideNav() {
    if (this.globalsService.getCurrentURL().indexOf("/supplier-portal") > -1) {
      return false;
    }

    if (this.globalsService.isSimple()) {
      if (this.globalsService.getCurrentURL().indexOf("reports-view") > -1) {
        return false;
      } else {
        return this.globalsService.getDatasetExternalID() == null;
      }
    } else {
      return !this.globalsService.getHideMenu();
    }
  }

  public configState(params: Params) {
    this.globalsService.log(
      `configState start [route: ${window.location.href}]`,
    );

    this.jwt = params["jwt"]; //JWT from Crimecast Admin
    this.ownerKey = params["k"]; // API Key
    this.externalAccountID = params["aid"]; // CCS Account ID
    this.hideMenu = params["h"] === "1";
    this.datasetExternalID = params["sid"]; // CCS Site ID

    if (!this.datasetExternalID) {
      this.datasetExternalID = params["datasetExternalID"]; // Also CCS Site ID.  TODO: Align?
    }

    this.targetID = params["id"]; // 'targetID' coming from a workflow
    this.ccsReportID = params["ccsrid"]; // crimecast reportID
    this.mode = params["m"];
    this.parent = params["page"];
    this.debug = params["d"] === "1";
    this.reportID = params["reportID"];
    this.reportToken = params["rt"];
    this.dashboardID = params["dashboardID"];
    this.impersonationMode = params["imp"];

    if (params["theme"] != null) {
      this.theme = params["theme"];
    }
    if (params["i"] != null) {
      this.iteration = parseInt(params["i"]);
      this.globalsService.setActiveDatasetIteration(this.iteration);
    }

    this.globalsService.setDatasetExternalID(this.datasetExternalID);

    if (this.hideMenu) {
      this.globalsService.setHideMenu(this.hideMenu);
    }
    if (this.jwt) {
      this.globalsService.setJWT(this.jwt);
    }
    if (this.ownerKey) {
      // from external system
      this.globalsService.setOwnerKey(this.ownerKey);
    }
    if (this.targetID) {
      this.globalsService.setActiveTargetID(this.targetID);
    }
    if (this.parent) {
      this.globalsService.setParent(this.parent);
    }
    if (this.dashboardID) {
      this.globalsService.setActiveDashboardID(this.dashboardID);
    }
    if (this.impersonationMode) {
      this.globalsService.setImpersonationMode(this.impersonationMode);
    }
    if (this.mode) {
      this.globalsService.setMode(this.mode);
    }

    // detect if standalone admin ui
    const standalone = window.self === window.top;
    this.globalsService.setStandalone(standalone);
    const href = window.location.href.toLowerCase();

    // default ccsFrom = 'app'
    // note: standalone reports-view means supplier portal
    if (href.indexOf("reports-view") > 0) {
      this.globalsService.setAdminUILogin(false);
      this.globalsService.setHideMenu(true);
      this.globalsService.setCCSFrom("reports-view");
    }

    // viewing within CCS
    if (standalone === false) {
      // set globally accessible property
      // indicating the referrer
      if (href.indexOf("designer") > 0) {
        this.globalsService.setAdminUILogin(false);
        this.globalsService.setHideMenu(true);
        this.globalsService.setCCSFrom("designer");
      } else if (href.indexOf("surveys")) {
        this.globalsService.setAdminUILogin(false);
        this.globalsService.setCCSFrom("surveys");
      }
    }

    this.globalsService.log("configState end");
  }

  public getActiveLink(): string {
    return this.activeLink;
  }

  public getColor(routerLink: string, queryParams: string) {
    const params = queryParams ? queryParams : "";
    return this.activeLink === routerLink + params ? "primary" : "";
  }

  public getExternalAccountID(): number {
    return this.externalAccountID;
  }

  /*
  public getPasswordEntryHtml(): string {
    return this.globalsService.getPasswordEntryHtml();
  }
  */

  public getQueryParams(feature: Feature): Object {
    return JSON.parse(feature.queryParams);
  }

  public getSidenavClass(): string {
    const sidenavClass = "sidenav-" + this.globalsService.getMode();
    // this.globalsService.log('getSidenavClass: ' + sidenavClass);
    return sidenavClass;
  }

  public handleWindowMessage(event: MessageEvent) {
    //console.log('handleWindowMessage:');
    //console.log(event);
    var prefix = `cewm: [app, received] `;
    var component = "";

    //this.globalsService.log(`${prefix} invoked...`);

    if (event.data && typeof event.data === "string") {
      const message = JSON.parse(event.data);
      if (message.key) {
        console.log(
          `selectedSite for key ${message.key} = ${message.selectedSite}`,
        );
        //if (this.globalsService.selectedLocMap.has(message.key)) {
        this.globalsService.selectedLocMap.set(
          message.key,
          message.selectedSite,
        );
        //}
        console.log(this.globalsService.selectedLocMap);
        return;
        //this.globalsService.setCcsSelectedSite(key, message.selectedSite);
      }

      var href = window.location.href;

      if (href.includes("reportID=-1")) {
        component = "menubutton";
        prefix += " @" + component;
      } else if (href.includes("reports-view")) {
        component = "report";
        prefix += " @" + component;
      } else if (href.includes("survey")) {
        component = "survey";
        prefix += " @" + component;
      }

      //this.globalsService.log(`${prefix} [event.data=${event.data}; href=${href}]`);

      const page = message.page;
      const reportMode = message.reportMode;
      const dashboardID = message.dashboardID;
      const datasetExternalID = message.datasetExternalID;

      // FOR LOCAL TEST ONLY
      /*
      const selectedSite = message.getSelectedSite;
      if (selectedSite) {
        var modulesSite = this.globalsService.getDatasetExternalID();
        //var m = `{"key":"${selectedSite}","selectedSite":${modulesSite}}`;
        var m = `{"key":"${selectedSite}","selectedSite":12345}`;
        var top = window.top;
        top.postMessage(m, '*');
        return;
      }
      */

      var pageLog = page ? `page=${page};` : "";
      var reportLog = reportMode ? `reportMode=${reportMode};` : "";
      var dashboardLog = dashboardID ? `dashboardID=${dashboardID};` : "";
      var siteLog = datasetExternalID ? `siteID=${datasetExternalID}` : "";

      this.globalsService.log(
        `${prefix} ${pageLog}${reportLog}${dashboardLog}${siteLog}`,
      );

      if (dashboardID) {
        this.globalsService.setActiveDashboardID(dashboardID);
      }
      if (datasetExternalID) {
        this.globalsService.setDatasetExternalID(datasetExternalID);
      }

      if (component == "menubutton") {
        this.globalsService.setCustomMenuState(
          this.elRef,
          page,
          reportMode,
          dashboardID,
          "handleWindowMessage",
        );
      }
    }
  }

  public isActiveLink(routerLink: string, queryParams: string): boolean {
    const params = queryParams ? queryParams : "";
    return this.getActiveLink() === routerLink + params;
  }

  public ngAfterViewInit() {
    this.globalsService.log("app.component: ngAfterViewInit()...");
    this.renderer.listen("window", "message", (event: MessageEvent) => {
      this.handleWindowMessage(event);
    });
  }

  public ngAfterViewChecked() {
    this.bindTemplateService.bindElements(this.elRef, "external-login", true);
  }

  public ngOnInit() {
    this.handleAppConnectivityChanges();
  }

  offlineEvent: Observable<Event>;
  onlineEvent: Observable<Event>;
  subscriptions: Subscription[] = [];

  private handleAppConnectivityChanges(): void {
    this.onlineEvent = fromEvent(window, "online");
    this.offlineEvent = fromEvent(window, "offline");

    this.subscriptions.push(
      this.onlineEvent.subscribe((e) => {
        // handle online mode
        console.log("Online...");
        this.globalsService.setConnected(true);
        this.globalsService.uploadLocalResponses();
        this.globalsService.uploadLocalErrors();
        if (this.isMenuButtonReport() === false) {
          this.globalsService.handleInfo(
            "Internet connectivity has been restored.",
            "Connection Restored",
          );
        }
      }),
    );

    this.subscriptions.push(
      this.offlineEvent.subscribe((e) => {
        // handle offline mode
        console.log("Offline...");
        this.globalsService.setConnected(false);

        if (this.isMenuButtonReport() === false) {
          this.globalsService.handleWarning(
            "Internet connectivity has been lost.",
            "No Connection",
          );
        }
      }),
    );
  }

  private isMenuButtonReport(): boolean {
    return this.globalsService.getCurrentURL().indexOf("reportID=-1") > 0;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
  // END: CONNECTIVITY CHECKS

  public ngDoCheck() {
    const change = this.differ.diff(this);

    if (change) {
      change.forEachChangedItem((item) => {
        // this.globalsService.log('app.component.ngDoCheck() change detected: ', item);

        // logs in based on query string api key OR owner session
        if (item.key === "ownerKey" && item.currentValue) {
          this.globalsService.log(
            "appComponent.ngDoCheck() calling geAuthKeyByKey...",
          );

          const ownerKey = item.currentValue;

          this.globalsService.log("getting auth key, showing loader...");
          this.loading = true;

          this.authKeyService.getAuthKeyByKey(ownerKey).then((authKey) => {
            if (authKey) {
              this.globalsService.log("got key.");

              this.globalsService.log(
                "appComponent.ngDoCheck() authKey retrieved, setting global ownerID ...",
              );

              // when ownerID != null, we're logged in.
              this.globalsService.setAuthKey(authKey);

              this.globalsService.setOwnerID(authKey.owner.id);
              this.globalsService.setOwnerDesignerID(authKey.owner.designerID);
              this.globalsService.setOwnerKey(authKey.key);
              this.globalsService.setLoggedInOwnerName(authKey.owner.name);
              this.globalsService.setMaintenance(authKey.owner.maintenance);
              this.globalsService.setMaintenanceHtml(
                authKey.owner.maintenanceHtml,
              );

              this.globalsService.setActiveRoleID(authKey.owner.roleID);

              if (authKey.respondent) {
                this.globalsService.setRespondentExternalID(
                  authKey.respondent.externalID,
                );
              } else {
                this.globalsService.log("no respondent...");
              }

              // load owner / user features
              this.globalsService.loadFeatureSet();

              // we're entering the designer
              if (this.reportID == null && this.targetID == null) {
                this.globalsService.log(
                  "No reportID or targetID, loading user features...",
                );

                const mode = this.globalsService.getMode(); // default = 0 (Survey)

                this.globalsService.log(
                  `ownerID: ${this.globalsService.getOwnerID()}; ownerDesignerD: ${this.globalsService.getOwnerDesignerID()}; ownerKey: ` +
                    `${this.globalsService.getOwnerKey()}; mode: ${this.globalsService.getMode()}`,
                );

                this.loading = false;

                this.globalsService.log("loading (authKey): " + this.loading);

                this.loading = true;
                this.ownerService.getRoleList().then((roleList) => {
                  this.globalsService.setRoleList(roleList);
                  this.loading = false;
                });
              } else {
                this.globalsService.log(
                  "has reportID or targetID, skipping feature load...",
                );
                this.loading = false;
              }
              this.globalsService.log(
                "ownerID: " +
                  this.globalsService.getOwnerID() +
                  "; ownerDesignerID: " +
                  this.globalsService.getOwnerDesignerID() +
                  "; ownerKey: " +
                  this.globalsService.getOwnerKey(),
              );
            } else {
              this.loading = false;
              this.globalsService.log("loading (no authKey): " + this.loading);
            }
          });
        }

        if (item.key === "debug" && item.currentValue) {
          this.globalsService.setDebug(this.debug);
        }
      });
    }
  }

  public setActiveLink(routerLink: string, queryParams: string) {
    const params = queryParams ? queryParams : "";
    this.activeLink = routerLink + params;
  }

  public setTheme() {
    this.globalsService.log("set theme to: " + this.theme);
    document.getElementById("appBody").className = this.theme;
  }
}
